import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { ArrowsOutSimple as Maximize2, Info } from 'phosphor-react';
import { ftToM, mToFt } from '../utils/units';

export const BridgeWidthInput = ({ width, onWidthChange, units = 'imperial' }) => {
  const [inputValue, setInputValue] = useState(width.toString());
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (units === 'metric') setInputValue(ftToM(width).toFixed(2));
    else setInputValue(width.toString());
  }, [width, units]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return setIsValid(false);
    if (units === 'imperial') {
      if (numValue < 6 || numValue > 40) setIsValid(false);
      else { setIsValid(true); onWidthChange(numValue); }
    } else {
      const min = 6 * 0.3048; const max = 40 * 0.3048;
      if (numValue < min || numValue > max) setIsValid(false);
      else { setIsValid(true); onWidthChange(mToFt(numValue)); }
    }
  };

  const presetFeet = [8, 10, 12, 14, 16, 20];
  const presetMeters = [2.4, 3.0, 3.6, 4.0, 4.5, 6.0];
  const presets = units === 'metric' ? presetMeters : presetFeet;

  return (
    <Card className="border-slate-600 bg-slate-800 shadow-xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100 flex items-center gap-2">
          <Maximize2 className="w-5 h-5 text-blue-400" />
          Bridge Width
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="width" className="text-slate-300">{units === 'metric' ? 'Total Width (meters)' : 'Total Width (feet)'}</Label>
          <Input id="width" type="number" value={inputValue} onChange={handleInputChange}
            className={`bg-slate-700 border-slate-600 text-slate-200 ${!isValid ? 'border-red-500 ring-red-500' : 'focus:border-blue-400 focus:ring-blue-400'}`}
            placeholder={units === 'metric' ? 'Enter width in meters' : 'Enter width in feet'}
            min={units === 'metric' ? (6 * 0.3048).toFixed(2) : 6}
            max={units === 'metric' ? (40 * 0.3048).toFixed(2) : 40}
            step={units === 'metric' ? '0.01' : '1'}
          />
          {!isValid && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <Info className="w-4 h-4" />
              {units === 'metric' ? 'Width must be between 1.83 and 12.19 meters' : 'Width must be between 6 and 40 feet'}
            </div>
          )}
          {isValid && width && (
            <div className="text-slate-400 text-sm">
              Current width: {units === 'metric' ? `${ftToM(width).toFixed(2)} m` : `${width} ft`}
            </div>
          )}
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-2 block">Quick Select ({units === 'metric' ? 'm' : 'ft'})</Label>
          <div className="grid grid-cols-3 gap-2">
            {presets.map((preset, idx) => (
              <Button key={idx} variant="outline" size="sm"
                className={`h-8 border-slate-600 text-slate-300 hover:bg-slate-600 ${
                  (units === 'metric' ? Math.abs(ftToM(width) - preset) < 1e-2 : width === preset) ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-700'
                }`}
                onClick={() => onWidthChange(units === 'metric' ? mToFt(preset) : preset)}>
                {units === 'metric' ? preset.toFixed(2) : preset}{units === 'metric' ? ' m' : "'"}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};